/**
 * Timezone Utility Functions
 * 
 * These utilities ensure consistent timezone handling across the application:
 * - Corn users: Eastern Time (EST/EDT)
 * - Cotton users: Central Time (CST/CDT)
 * 
 * The logic converts a date to midnight in the local timezone, then to UTC.
 * This prevents date shift issues when users submit data late in the day.
 */

import { toZonedTime, fromZonedTime } from "date-fns-tz";

/**
 * Get midnight in Eastern timezone as UTC ISO string
 * Used for: Corn irrigation, nitrogen, marketing options
 * 
 * Example: User selects Jan 8, 2026
 * -> Jan 8 00:00:00 EST
 * -> Jan 8 05:00:00 UTC (during EST) or 04:00:00 UTC (during EDT)
 */
export const getEasternMidnightUTC = () => {
  const timeZone = "America/New_York";
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  const localMidnight = new Date(year, month, day, 0, 0, 0);
  const zoned = toZonedTime(localMidnight, timeZone);
  return new Date(zoned).toISOString();
};

/**
 * Convert a date string to midnight Eastern timezone as UTC ISO string
 * Used when user selects a specific date in the datepicker
 * 
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} UTC ISO string
 */
export const convertToEasternMidnightUTC = (dateString) => {
  const timeZone = "America/New_York";
  const now = new Date(dateString);
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  const localMidnight = new Date(year, month, day, 0, 0, 0);
  const zoned = toZonedTime(localMidnight, timeZone);
  return new Date(zoned).toISOString();
};

/**
 * Get midnight in Central timezone as UTC ISO string
 * Used for: Cotton irrigation, nitrogen, growth regulation, marketing options
 * 
 * Example: User selects Jan 8, 2026
 * -> Jan 8 00:00:00 CST
 * -> Jan 8 06:00:00 UTC (during CST) or 05:00:00 UTC (during CDT)
 */
export const getCentralMidnightUTC = () => {
  const timeZone = "America/Chicago";
  const now = new Date();
  const dateOnly = now.toLocaleDateString("en-CA"); // YYYY-MM-DD
  const localMidnight = new Date(`${dateOnly}T00:00:00`);
  return fromZonedTime(localMidnight, timeZone).toISOString();
};

/**
 * Convert a date string to midnight Central timezone as UTC ISO string
 * Used when user selects a specific date in the datepicker
 * 
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} UTC ISO string
 */
export const convertToCentralMidnightUTC = (dateString) => {
  const timeZone = "America/Chicago";
  const localMidnight = new Date(`${dateString}T00:00:00`);
  return fromZonedTime(localMidnight, timeZone).toISOString();
};
